import React from "react";
import { useLang } from "../context/LangContext";
import { Stethoscope, Activity, Settings } from "lucide-react";

const COPY = {
  fr: {
    overline: "ÉQUIPE DÉDIÉE · CHAQUE INTERVENTION",
    title: "Une équipe médicale complète pour chaque session.",
    note: "Trois professionnels présents systématiquement, en yacht, villa privée ou événement privé.",
    roles: [
      { icon: Stethoscope, k: "01", role: "1 Médecin", desc: "Diagnostic, supervision médicale et adaptation du protocole." },
      { icon: Activity, k: "02", role: "1 Kinésithérapeute", desc: "Mobilité, mobilisations, suivi fonctionnel post-session." },
      { icon: Settings, k: "03", role: "1 Opérateur", desc: "Manipulation des technologies, monitoring et logistique d'intervention." },
    ],
  },
  en: {
    overline: "DEDICATED TEAM · EVERY SESSION",
    title: "A complete medical team for every session.",
    note: "Three professionals present systematically, on yacht, private villa or private event.",
    roles: [
      { icon: Stethoscope, k: "01", role: "1 Doctor", desc: "Diagnosis, medical supervision and protocol adaptation." },
      { icon: Activity, k: "02", role: "1 Physiotherapist", desc: "Mobility, mobilisations, post-session functional follow-up." },
      { icon: Settings, k: "03", role: "1 Operator", desc: "Technology handling, monitoring and on-site logistics." },
    ],
  },
  it: {
    overline: "TEAM DEDICATO · OGNI INTERVENTO",
    title: "Un team medico completo per ogni sessione.",
    note: "Tre professionisti presenti sistematicamente, su yacht, villa privata o evento privato.",
    roles: [
      { icon: Stethoscope, k: "01", role: "1 Medico", desc: "Diagnosi, supervisione medica e adattamento del protocollo." },
      { icon: Activity, k: "02", role: "1 Fisioterapista", desc: "Mobilità, mobilizzazioni, follow-up funzionale post-sessione." },
      { icon: Settings, k: "03", role: "1 Operatore", desc: "Gestione delle tecnologie, monitoraggio e logistica di intervento." },
    ],
  },
  ru: {
    overline: "СПЕЦИАЛИЗИРОВАННАЯ КОМАНДА · КАЖДЫЙ СЕАНС",
    title: "Полная медицинская команда на каждом сеансе.",
    note: "Трое специалистов присутствуют систематически — на яхте, в частной вилле или на частном мероприятии.",
    roles: [
      { icon: Stethoscope, k: "01", role: "1 Врач", desc: "Диагностика, медицинский контроль и адаптация протокола." },
      { icon: Activity, k: "02", role: "1 Физиотерапевт", desc: "Подвижность, мобилизация, функциональное сопровождение после сеанса." },
      { icon: Settings, k: "03", role: "1 Оператор", desc: "Управление технологиями, мониторинг и логистика на месте." },
    ],
  },
};

export default function TeamComposition() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  return (
    <section className="bg-[var(--surface-2)] hairline-t hairline-b py-16 md:py-20" data-testid="team-composition">
      <div className="container-luxe">
        <div className="max-w-3xl">
          <div className="overline mb-3">{c.overline}</div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light text-[var(--ink)] leading-[1.1]">
            {c.title}
          </h2>
          <p className="mt-3 text-[14px] text-[var(--text-secondary)] font-body leading-relaxed">{c.note}</p>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px hairline">
          {c.roles.map((r) => {
            const Icon = r.icon;
            return (
              <div key={r.k} className="bg-[var(--surface-1)] p-7 flex flex-col min-h-[200px]" data-testid={`team-role-${r.k}`}>
                <div className="flex items-center justify-between">
                  <Icon size={20} className="text-[var(--gold)]" />
                  <span className="overline">{r.k}</span>
                </div>
                <div className="mt-auto pt-6">
                  <div className="font-display text-xl text-[var(--ink)] font-light">{r.role}</div>
                  <p className="mt-2 text-[13px] text-[var(--text-secondary)] leading-relaxed font-body">{r.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
